// src/pages/client/ClientBillingPage.tsx
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Card from '../../components/Card';
import Button from '../../components/Button';
import PaymentMethodsPanel from '../../components/billing/PaymentMethodsPanel';
import BillingHistoryTable from '../../components/billing/BillingHistoryTable';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../../lib/stripePublic';
import type { AppDispatch, RootState } from '../../store';
import { getSubscription } from '../../store/features/billing/billingSlice';
import { gradientPillCss } from '../../styles/gradientPill';

const Wrap = styled.div`
  display: grid;
  place-items: start center;
  padding: 2rem 1rem;
`;
const Grid = styled.div`
  width: 100%;
  max-width: 1120px;
  display: grid;
  gap: 1rem;
  grid-template-columns: 1.2fr .8fr;

  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;
const HeaderRow = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;
const HeaderTitle = styled.h2`margin: 0 0 .25rem; font-weight: 800; color: #e0a15e;`;
const HeaderMuted = styled.p`margin: 0; color: #9AA4B2;`;
const StatusChip = styled.div`
  min-height: 32px;
  padding: 0 0.9rem;
  border-radius: 999px;
  border: 1px solid rgba(101, 137, 99, 0.34);
  background: rgba(101, 137, 99, 0.16);
  color: #658963;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;
const SubscriptionGrid = styled.div`
  display: grid;
  gap: 0.625rem 1.25rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 0.875rem;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;
const DetailItem = styled.div`
  display: grid;
  gap: 0.125rem;
  min-width: 0;
`;
const FullWidthDetailItem = styled(DetailItem)`
  grid-column: 1 / -1;
`;
const SpacedHeaderMuted = styled(HeaderMuted)`
  margin-top: 1.15rem;
  color: #e0a15e;
`;
const DetailLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9aa4b2;
`;
const DetailValue = styled.span`
  font-size: 0.95rem;
  font-weight: 700;
  color: #e8eaed;
  word-break: break-word;
`;
const DetailList = styled.div`
  display: grid;
  gap: 0.3rem;
`;
const TwoColumnDetailList = styled(DetailList)`
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 3rem;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;
const DetailListItem = styled.div`
  color: #e8eaed;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.35;
`;
const SpacedDetailLabel = styled(DetailLabel)`
  margin-top: 0.85rem;
`;
const PurchaseSummaryWrap = styled.div`
  margin-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
  padding-top: 0.9rem;
  display: grid;
  gap: 0.65rem;
`;
const PurchaseSummaryTitle = styled.h3`
  margin: 0;
  font-size: 0.98rem;
  font-weight: 800;
  color: #e0a15e;
`;
const PurchaseSummaryHeadingGroup = styled.div`
  display: grid;
  gap: 0.2rem;
  min-width: 0;
`;
const PurchaseSummaryHeaderRow = styled.div`
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;
const PurchaseSummaryHeaderMeta = styled.div`
  display: grid;
  justify-items: end;
  gap: 0.15rem;
  text-align: right;
`;
const PurchaseSummaryMeta = styled.p`
  margin: 0;
  color: #9aa4b2;
  font-size: 0.84rem;
`;
const PurchaseSummaryDetailsRow = styled.div`
  display: grid;
  gap: 0.7rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  
`;
const PurchaseSummaryDetail = styled.div`
  display: grid;
  gap: 0.18rem;
  min-width: 0;
  justify-content: center;
`;
const PurchaseLineItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.4rem 0.8rem;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: 10px;
  padding: 0.5rem 0.65rem;
`;
const PurchaseLineTitle = styled.span`
  color: #e8eaed;
  font-size: 0.87rem;
  font-weight: 700;
`;
const PurchaseLineMeta = styled.span`
  color: #9aa4b2;
  font-size: 0.8rem;
  font-weight: 600;
`;
const PurchaseLineValue = styled.span`
  color: #e8eaed;
  font-size: 0.84rem;
  font-weight: 700;
`;
const ManageServicesButton = styled(Button)`
  text-transform: uppercase;
  ${gradientPillCss};
`;

const PLAN_ID_TO_LABEL: Record<string, string> = {
  marketing: 'Managed Marketing',
  'digital-services': 'Assets & Design',
  'ecommerce-digital-services': 'Platform Development',
};

const toUnix = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const resolvePlanLabel = (sub: any): string => {
  const fromPurchaseSummary =
    typeof sub?.purchaseSummary?.serviceName === 'string'
      ? sub.purchaseSummary.serviceName.trim()
      : '';
  if (fromPurchaseSummary) return fromPurchaseSummary;

  const planDisplayName =
    typeof sub?.planDisplayName === 'string' ? sub.planDisplayName.trim() : '';
  if (planDisplayName) return planDisplayName;

  const metadataPlanName =
    typeof sub?.metadata?.planName === 'string' ? sub.metadata.planName.trim() : '';
  if (metadataPlanName) return metadataPlanName;

  const planId =
    typeof sub?.planId === 'string' && sub.planId.trim().length > 0
      ? sub.planId.trim()
      : (typeof sub?.metadata?.planId === 'string' ? sub.metadata.planId.trim() : '');
  if (planId) return PLAN_ID_TO_LABEL[planId] || planId;

  const price = sub?.items?.data?.[0]?.price ?? sub?.items?.[0]?.price;
  const nickname = typeof price?.nickname === 'string' ? price.nickname.trim() : '';
  if (nickname) return nickname;

  const productName =
    price?.product &&
    typeof price.product === 'object' &&
    typeof price.product.name === 'string'
      ? price.product.name.trim()
      : '';
  if (productName) return productName;

  return 'Active subscription';
};

export default function ClientBillingPage() {
  const nav = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { subscription, loading } = useSelector((s: RootState) => s.billing);

  useEffect(() => {
    dispatch(getSubscription());
  }, [dispatch]);

  const statusLabel = useMemo(() => {
    const status = (subscription as any)?.status as string | undefined;
    if (!status) return 'No active subscription';
    return status.replace(/_/g, ' ');
  }, [subscription]);

  const planLabel = useMemo(() => {
    const sub: any = subscription || null;
    if (!sub) return 'Not set';
    return resolvePlanLabel(sub);
  }, [subscription]);

  const recurringAmount = useMemo(() => {
    const sub: any = subscription || {};
    const price = sub?.items?.data?.[0]?.price ?? sub?.items?.[0]?.price;
    const amountCents =
      typeof price?.unit_amount === 'number'
        ? price.unit_amount
        : (typeof price?.unit_amount_decimal === 'string'
          ? Math.round(parseFloat(price.unit_amount_decimal))
          : null);
    if (typeof amountCents !== 'number') {
      const recurringFromSummary = Number(sub?.purchaseSummary?.recurringMonthlyCents);
      if (!Number.isFinite(recurringFromSummary) || recurringFromSummary < 0) return 'Not set';
      return `$${(Math.round(recurringFromSummary) / 100).toFixed(2)} USD / month`;
    }

    const currency = String(price?.currency || 'usd').toUpperCase();
    const recurring = price?.recurring;
    const interval = recurring?.interval;
    const intervalCount = Number(recurring?.interval_count || 1);
    const intervalSuffix = interval
      ? ` / ${intervalCount > 1 ? `${intervalCount} ${interval}s` : interval}`
      : '';
    return `$${(amountCents / 100).toFixed(2)} ${currency}${intervalSuffix}`;
  }, [subscription]);

  const nextBillingDate = useMemo(() => {
    const sub: any = subscription || {};
    if (!sub) return 'Not set';

    const ts =
      toUnix(sub?.nextBillingAt) ??
      toUnix(sub?.cancel_at) ??
      (sub?.status === 'trialing'
        ? toUnix(sub?.trial_end)
        : toUnix(sub?.current_period_end)) ??
      toUnix(sub?.items?.data?.[0]?.current_period_end);
    if (!ts) return 'Not set';

    return new Date(ts * 1000).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, [subscription]);

  const purchaseLineItems = useMemo(() => {
    const lines = (subscription as any)?.purchaseSummary?.lineItems;
    if (!Array.isArray(lines)) return [] as any[];
    return lines.filter((line) => line && typeof line === 'object');
  }, [subscription]);

  const purchaseAddOns = useMemo(() => {
    const lines = (subscription as any)?.purchaseSummary?.addOns;
    if (!Array.isArray(lines)) return [] as any[];
    return lines.filter((line) => line && typeof line === 'object');
  }, [subscription]);

  const laneLineItems = useMemo(() => {
    if (!purchaseLineItems.length) return [] as any[];
    return purchaseLineItems.filter((line: any) =>
      typeof line?.name === 'string' ? /lane/i.test(line.name) : false,
    );
  }, [purchaseLineItems]);

  const purchaseSummary = useMemo(() => {
    const summary = (subscription as any)?.purchaseSummary;
    if (!summary || typeof summary !== 'object') return null;
    return summary as Record<string, any>;
  }, [subscription]);

  const serviceTierLabel = useMemo(() => {
    const tierName =
      typeof purchaseSummary?.tierName === 'string'
        ? purchaseSummary.tierName.trim()
        : '';
    if (tierName) return tierName;
    const tierSkuId =
      typeof purchaseSummary?.tierSkuId === 'string'
        ? purchaseSummary.tierSkuId.trim()
        : '';
    if (tierSkuId) return tierSkuId.toUpperCase();
    return 'Not set';
  }, [purchaseSummary]);

  const selectedOfferings = useMemo(() => {
    const offerings = purchaseSummary?.selectedServiceOfferings;
    const fromSummary: unknown[] = Array.isArray(offerings) ? offerings : [];
    const normalizedFromSummary = fromSummary
      .map((entry: unknown) => String(entry || '').trim())
      .filter(Boolean);
    if (normalizedFromSummary.length > 0) return normalizedFromSummary;

    return laneLineItems
      .map((item: any) => String(item?.name || '').trim())
      .filter(Boolean);
  }, [laneLineItems, purchaseSummary]);

  const selectedAddOns = useMemo(() => {
    const addOnNames = purchaseSummary?.addOnNames;
    const fromSummary: unknown[] = Array.isArray(addOnNames) ? addOnNames : [];
    const normalizedFromSummary = fromSummary
      .map((entry: unknown) => String(entry || '').trim())
      .filter(Boolean);
    if (normalizedFromSummary.length > 0) return normalizedFromSummary;

    return purchaseAddOns
      .map((item: any) => String(item?.name || '').trim())
      .filter(Boolean);
  }, [purchaseAddOns, purchaseSummary]);

  const contractTermLabel = useMemo(() => {
    const fromSummary =
      typeof purchaseSummary?.contractTerm === 'string'
        ? purchaseSummary.contractTerm.trim()
        : '';
    if (fromSummary) return fromSummary;
    const hasRecurring = purchaseLineItems.some(
      (item: any) => String(item?.billingType || '').toLowerCase() === 'recurring',
    );
    return hasRecurring ? 'Month-to-month' : 'Not set';
  }, [purchaseLineItems, purchaseSummary]);

  const contractedCapacityLabel = useMemo(() => {
    const minRaw = Number((purchaseSummary as any)?.contractedMonthlyMinutesMin);
    const maxRaw = Number((purchaseSummary as any)?.contractedMonthlyMinutesMax);
    const minHours =
      Number.isFinite(minRaw) && minRaw > 0 ? Math.round((minRaw / 60) * 10) / 10 : null;
    const maxHours =
      Number.isFinite(maxRaw) && maxRaw > 0 ? Math.round((maxRaw / 60) * 10) / 10 : null;

    if (minHours !== null && maxHours !== null) {
      return minHours === maxHours
        ? `${minHours} hrs / month`
        : `${minHours}-${maxHours} hrs / month`;
    }
    if (minHours !== null) return `${minHours}+ hrs / month`;
    if (maxHours !== null) return `Up to ${maxHours} hrs / month`;
    return 'Not set';
  }, [purchaseSummary]);

  const clientSuccessMeetingsLabel = useMemo(() => {
    const summary = purchaseSummary as any;
    const entitlements =
      summary?.meetingEntitlements &&
      typeof summary.meetingEntitlements === 'object' &&
      !Array.isArray(summary.meetingEntitlements)
        ? summary.meetingEntitlements
        : {};

    const cadenceRaw =
      typeof entitlements.clientSuccessCadenceType === 'string'
        ? entitlements.clientSuccessCadenceType.trim()
        : typeof summary?.clientSuccessCadenceType === 'string'
          ? summary.clientSuccessCadenceType.trim()
          : '';
    const cadenceType = cadenceRaw.toLowerCase();

    const monthlyRaw =
      entitlements.clientSuccessMeetingsPerMonth ?? summary?.clientSuccessMeetingsPerMonth;
    const quarterlyRaw =
      entitlements.clientSuccessMeetingsPerQuarter ?? summary?.clientSuccessMeetingsPerQuarter;

    const monthlyMeetings =
      typeof monthlyRaw === 'number'
        ? monthlyRaw
        : typeof monthlyRaw === 'string' && monthlyRaw.trim()
          ? Number.parseInt(monthlyRaw, 10)
          : null;
    const quarterlyMeetings =
      typeof quarterlyRaw === 'number'
        ? quarterlyRaw
        : typeof quarterlyRaw === 'string' && quarterlyRaw.trim()
          ? Number.parseInt(quarterlyRaw, 10)
          : null;

    if (typeof monthlyMeetings === 'number' && Number.isFinite(monthlyMeetings)) {
      if (monthlyMeetings <= 0) return 'None included';
      return `${monthlyMeetings} / month`;
    }

    if (typeof quarterlyMeetings === 'number' && Number.isFinite(quarterlyMeetings)) {
      if (quarterlyMeetings <= 0) return 'None included';
      return `${quarterlyMeetings} / quarter`;
    }

    if (cadenceType === 'weekly') return '4 / month';
    if (cadenceType === 'biweekly') return '2 / month';
    if (cadenceType === 'monthly') return '1 / month';
    if (cadenceType === 'quarterly') return '1 / quarter';

    return 'Not set';
  }, [purchaseSummary]);

  const consumedCapacityLabel = useMemo(() => {
    const raw = Number((purchaseSummary as any)?.consumedBillableMinutes);
    if (!Number.isFinite(raw) || raw <= 0) return '0 hrs tracked';
    const hours = Math.round((raw / 60) * 10) / 10;
    return `${hours} hrs tracked`;
  }, [purchaseSummary]);

  const remainingCapacityLabel = useMemo(() => {
    const minRaw = Number((purchaseSummary as any)?.remainingBillableMinutesMin);
    const maxRaw = Number((purchaseSummary as any)?.remainingBillableMinutesMax);
    const minHours =
      Number.isFinite(minRaw) && minRaw >= 0 ? Math.round((minRaw / 60) * 10) / 10 : null;
    const maxHours =
      Number.isFinite(maxRaw) && maxRaw >= 0 ? Math.round((maxRaw / 60) * 10) / 10 : null;

    if (minHours !== null && maxHours !== null) {
      return minHours === maxHours
        ? `${minHours} hrs remaining`
        : `${minHours}-${maxHours} hrs remaining`;
    }
    if (minHours !== null) return `${minHours}+ hrs remaining`;
    if (maxHours !== null) return `Up to ${maxHours} hrs remaining`;
    return 'Not set';
  }, [purchaseSummary]);

  const blockedCapacityLabel = useMemo(() => {
    const raw = Number((purchaseSummary as any)?.blockedBillableMinutes);
    if (!Number.isFinite(raw) || raw <= 0) return '0 hrs paused';
    const hours = Math.round((raw / 60) * 10) / 10;
    return `${hours} hrs paused`;
  }, [purchaseSummary]);

  const plannedRemainingWorkLabel = useMemo(() => {
    const raw = Number((purchaseSummary as any)?.plannedRemainingEstimateMinutes);
    if (!Number.isFinite(raw) || raw <= 0) return '0 hrs planned';
    const hours = Math.round((raw / 60) * 10) / 10;
    return `${hours} hrs planned`;
  }, [purchaseSummary]);

  const targetPerformanceLabel = useMemo(() => {
    const within = Number((purchaseSummary as any)?.withinTargetCount);
    const outside = Number((purchaseSummary as any)?.outsideTargetCount);
    const safeWithin = Number.isFinite(within) && within > 0 ? Math.round(within) : 0;
    const safeOutside = Number.isFinite(outside) && outside > 0 ? Math.round(outside) : 0;
    if (!safeWithin && !safeOutside) return 'No completed requests yet';
    return `${safeWithin} within goal / ${safeOutside} outside goal`;
  }, [purchaseSummary]);

  const beneficiaryOrganization = useMemo(() => {
    const value = (purchaseSummary as any)?.beneficiaryOrganization;
    if (!value || typeof value !== 'object') return null;
    const orgName =
      typeof (value as any).orgName === 'string' ? (value as any).orgName.trim() : '';
    const orgId = typeof (value as any).orgId === 'string' ? (value as any).orgId.trim() : '';
    if (!orgName && !orgId) return null;
    return orgName || orgId;
  }, [purchaseSummary]);

  const inheritedFromOrganization = useMemo(() => {
    const inheritance = (subscription as any)?.inheritedFromOrganization;
    if (!inheritance || typeof inheritance !== 'object') return null;
    const orgName =
      typeof (inheritance as any).orgName === 'string'
        ? (inheritance as any).orgName.trim()
        : '';
    const orgId =
      typeof (inheritance as any).orgId === 'string'
        ? (inheritance as any).orgId.trim()
        : '';
    if (!orgName && !orgId) return null;
    return {
      orgName: orgName || orgId,
      orgId,
    };
  }, [subscription]);

  const slaPolicySummaries = useMemo(() => {
    const slaPolicies = purchaseSummary?.slaPolicies;
    const policies = Array.isArray(slaPolicies) ? slaPolicies : [];
    return policies
      .map((policy: any) => {
        const name = String(policy?.name || '').trim();
        if (!name) return '';
        const responseTarget = Number(policy?.responseTargetMinutes);
        const resolutionTarget = Number(policy?.resolutionTargetMinutes);
        const targetCompletion = Number(policy?.targetCompletionMinutes);
        const pieces: string[] = [];
        if (Number.isFinite(responseTarget) && responseTarget > 0) {
          pieces.push(`response ${Math.round(responseTarget)}m`);
        }
        if (Number.isFinite(resolutionTarget) && resolutionTarget > 0) {
          pieces.push(`resolution ${Math.round(resolutionTarget)}m`);
        }
        if (Number.isFinite(targetCompletion) && targetCompletion > 0) {
          pieces.push(`goal ${Math.round(targetCompletion)}m`);
        }
        return pieces.length ? `${name} (${pieces.join(', ')})` : name;
      })
      .filter(Boolean);
  }, [purchaseSummary]);

  return (
    <Wrap>
      <Grid>
        {/* Left column: invoices */}
        <div style={{ display: 'grid', gap: '1rem' }}>
          <BillingHistoryTable />
        </div>

        {/* Right column: in-app card management */}
        <Elements stripe={stripePromise}>
          <PaymentMethodsPanel />
        </Elements>
      </Grid>

      <Card style={{ width: '100%', maxWidth: 1120, marginTop: '1rem' }}>
        <HeaderRow>
          <HeaderTitle>Billing & Payments</HeaderTitle>
          <StatusChip>{statusLabel}</StatusChip>
        </HeaderRow>
        {loading && !subscription ? (
          <HeaderMuted>Loading current subscription details...</HeaderMuted>
        ) : (
          <>
            {purchaseLineItems.length > 0 ? (
              <PurchaseSummaryWrap style={{ marginTop: '0.75rem' }}>
                <PurchaseSummaryHeaderRow>
                  <PurchaseSummaryHeadingGroup>
                    <PurchaseSummaryTitle>Purchased Configuration</PurchaseSummaryTitle>
                    {beneficiaryOrganization ? (
                      <PurchaseSummaryMeta>
                        Contract beneficiary: {beneficiaryOrganization}
                      </PurchaseSummaryMeta>
                    ) : null}
                  </PurchaseSummaryHeadingGroup>
                  <PurchaseSummaryHeaderMeta>
                    <DetailLabel>
                      {(subscription as any)?.status === 'trialing' ? 'Trial Ends' : 'Next Billing Date'}
                    </DetailLabel>
                    <DetailValue>{nextBillingDate}</DetailValue>
                  </PurchaseSummaryHeaderMeta>
                </PurchaseSummaryHeaderRow>

                {purchaseLineItems.map((item: any) => {
                  const amountCents = Number(item?.discountedLineSubtotalCents);
                  const amountLabel =
                    Number.isFinite(amountCents) && amountCents >= 0
                      ? `$${(Math.round(amountCents) / 100).toFixed(2)}`
                      : '--';
                  const quantity = Number(item?.quantity);
                  const quantityLabel =
                    Number.isFinite(quantity) && quantity > 0 ? `x${Math.floor(quantity)}` : 'x1';
                  const billingType =
                    typeof item?.billingType === 'string' ? item.billingType : 'recurring';
                  return (
                    <PurchaseLineItem key={`${item?.skuId || item?.name || 'line'}-${quantityLabel}`}>
                      <div style={{ display: 'grid', gap: '0.15rem' }}>
                        <PurchaseLineTitle>{String(item?.name || 'Line item')}</PurchaseLineTitle>
                        <PurchaseLineMeta>
                          {`${String(item?.skuId || '').toUpperCase()} ${quantityLabel} - ${billingType}`}
                        </PurchaseLineMeta>
                      </div>
                      <PurchaseLineValue>{amountLabel}</PurchaseLineValue>
                    </PurchaseLineItem>
                  );
                })}
                <PurchaseSummaryDetailsRow>
                  <PurchaseSummaryDetail>
                    <DetailLabel>Contract Term</DetailLabel>
                    <DetailValue>{contractTermLabel}</DetailValue>
                  </PurchaseSummaryDetail>
                  <PurchaseSummaryDetail>
                    <DetailLabel>Contract Capacity</DetailLabel>
                    <DetailValue>{contractedCapacityLabel}</DetailValue>
                  </PurchaseSummaryDetail>
                  <PurchaseSummaryDetail>
                    <DetailLabel>Client Success Meetings</DetailLabel>
                    <DetailValue>{clientSuccessMeetingsLabel}</DetailValue>
                  </PurchaseSummaryDetail>
                </PurchaseSummaryDetailsRow>
                {laneLineItems.length ? (
                  <PurchaseSummaryMeta>
                    {`Lane items: ${laneLineItems.map((item: any) => String(item?.name || '').trim()).filter(Boolean).join(', ')}`}
                  </PurchaseSummaryMeta>
                ) : null}
                {slaPolicySummaries.length ? (
                  <PurchaseSummaryMeta>
                    {`SLA policies: ${slaPolicySummaries.join(' | ')}`}
                  </PurchaseSummaryMeta>
                ) : null}
              </PurchaseSummaryWrap>
            ) : null}
            <SpacedHeaderMuted>
              {subscription
                ? 'Current subscription details for this account.'
                : 'No active subscription found. Select a plan to start service.'}
            </SpacedHeaderMuted>
            <SubscriptionGrid>
              <FullWidthDetailItem>
                <DetailLabel>Service Offerings</DetailLabel>
                {selectedOfferings.length ? (
                  <TwoColumnDetailList>
                    {selectedOfferings.map((offering, index) => (
                      <DetailListItem key={`${offering}-${index}`}>{offering}</DetailListItem>
                    ))}
                  </TwoColumnDetailList>
                ) : (
                  <DetailValue>None selected</DetailValue>
                )}
                <SpacedDetailLabel>Add-ons</SpacedDetailLabel>
                {selectedAddOns.length ? (
                  <DetailList>
                    {selectedAddOns.map((addOn, index) => (
                      <DetailListItem key={`${addOn}-${index}`}>{addOn}</DetailListItem>
                    ))}
                  </DetailList>
                ) : (
                  <DetailValue>None selected</DetailValue>
                )}
              </FullWidthDetailItem>
            </SubscriptionGrid>
            {inheritedFromOrganization ? (
              <PurchaseSummaryMeta style={{ marginTop: '0.75rem' }}>
                Service is inherited from organization: {inheritedFromOrganization.orgName}
              </PurchaseSummaryMeta>
            ) : null}
          </>
        )}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end', marginTop: '1rem' }}>
          <ManageServicesButton onClick={() => nav('/dashboard/services')}>
            Manage services
          </ManageServicesButton>
        </div>
      </Card>
    </Wrap>
  );
}
